import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, ArrowRight, ArrowDown, Brain, Bot, Users, GraduationCap, TrendingUp,
  BookOpen, GitCompare, Award, Briefcase, HeartHandshake, ShieldCheck, Lightbulb,
  Network, Target, Compass, Cpu, Building2,
} from "lucide-react";

const SITE = "https://ai.entrancezone.com";

const AI_POWERS = [
  "Understand interests & aptitude", "Identify skills & strengths", "Discover career options",
  "Compare courses & colleges", "Analyse entrance-exam data", "Predict possible rank / college outcomes",
  "Discover scholarships & internships", "Build personalized career roadmaps", "Answer career questions 24/7",
];
const HUMAN_POWERS = [
  "Personal, contextual guidance", "One-to-one counselling", "Career decision support",
  "Course & college recommendations", "Parent–student guidance", "Application guidance", "Judgment beyond data",
];

const GRAPH = [
  "Student", "Interests", "Aptitude", "Skills", "Careers", "Courses",
  "Colleges", "Entrance Exams", "Scholarships", "Internships", "Jobs", "Career Outcomes",
];

const FEATURES = [
  { icon: Brain, title: "AI Career Assessment", desc: "Discover interests, aptitude, personality, strengths and potential career directions.", color: "cyan" },
  { icon: Bot, title: "AI Career Counselling", desc: "Ask career questions and receive personalized AI guidance, anytime.", color: "indigo" },
  { icon: HeartHandshake, title: "Human Career Counsellors", desc: "Connect with experienced counsellors by specialization, reviews and your needs.", color: "emerald" },
  { icon: Building2, title: "College Predictor", desc: "Explore colleges based on rank, score, category and relevant admission factors.", color: "amber" },
  { icon: TrendingUp, title: "Rank Predictor", desc: "Analyse historical exam data to estimate possible rank ranges.", color: "cyan" },
  { icon: BookOpen, title: "Course Finder", desc: "Discover courses aligned with your interests, skills and career goals.", color: "rose" },
  { icon: GitCompare, title: "College Comparison", desc: "Compare fees, placements, academics, infrastructure, location & ROI.", color: "indigo" },
  { icon: Award, title: "Scholarships", desc: "Discover relevant scholarship opportunities you may qualify for.", color: "emerald" },
  { icon: Briefcase, title: "Internships", desc: "Explore internships aligned with your skills and career interests.", color: "amber" },
  { icon: Users, title: "Parent Dashboard", desc: "Help parents understand strengths, options, colleges and education planning.", color: "cyan" },
];

const colorMap = {
  cyan: "from-cyan-400/20 to-cyan-500/5 text-cyan-300 border-cyan-400/20",
  indigo: "from-indigo-400/20 to-indigo-500/5 text-indigo-300 border-indigo-400/20",
  emerald: "from-emerald-400/20 to-emerald-500/5 text-emerald-300 border-emerald-400/20",
  amber: "from-amber-400/20 to-amber-500/5 text-amber-300 border-amber-400/20",
  rose: "from-rose-400/20 to-rose-500/5 text-rose-300 border-rose-400/20",
};

const PLACEHOLDER_TEAM = [
  { role: "Founder & Career Strategist" }, { role: "Career Counsellors" }, { role: "STEM Admissions Experts" }, { role: "Medical Admissions Experts" },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay }} className={className}>
    {children}
  </motion.div>
);

const Eyebrow = ({ children }) => (
  <span className="font-mono-data text-xs uppercase tracking-[0.25em] text-cyan-400">{children}</span>
);

export default function AboutPage() {
  const [team, setTeam] = useState(null);
  useEffect(() => { api.get("/mentors").then((r) => setTeam(r.data)).catch(() => setTeam([])); }, []);
  const TEAM = team === null ? [] : team.length ? team : PLACEHOLDER_TEAM;
  const navigate = useNavigate();
  const { user } = useAuth();
  const startAssessment = () => navigate(user ? "/assessment" : "/auth");
  const exploreCounsellors = () => navigate(user ? "/counsellors" : "/auth");

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white overflow-x-hidden" data-testid="about-page">
      <Seo
        title="AI-Powered Career Intelligence & Career Counselling"
        path="/about"
        description="Architect your future career with AI + human mentors. Explore AI career assessment, career counselling, college & course guidance, college predictors, scholarships and personalized career intelligence with EntranceZone AI."
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "name": "EntranceZone AI",
              "url": SITE + "/",
              "logo": SITE + "/og-image.jpg",
              "description": "AI-powered career intelligence platform launched by EntranceZone — combining AI career counselling, human mentors, assessments, predictors and career planning for students and parents.",
              "foundingDate": "2026",
              "sameAs": [],
              "knowsAbout": ["AI Career Counselling", "Career Assessment", "College Predictor", "Rank Predictor", "Scholarships", "Career Planning"],
            },
            {
              "@type": "WebSite",
              "name": "EntranceZone AI",
              "url": SITE + "/",
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE + "/" },
                { "@type": "ListItem", "position": 2, "name": "About Us", "item": SITE + "/about" },
              ],
            },
          ],
        }}
      />

      {/* Nav */}
      <header className="sticky top-0 z-50 glass">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 flex items-center justify-between py-4">
          <Logo />
          <div className="flex items-center gap-3">
            <button data-testid="about-nav-signin-btn" onClick={() => navigate("/auth")}              className="hidden sm:inline text-sm font-medium text-slate-300 hover:text-white transition-colors px-3 py-2">
              Sign in
            </button>
            <button data-testid="about-nav-getstarted-btn" onClick={startAssessment}
              className="rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#041016] transition-transform hover:scale-105 glow-cyan">
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative radial-fade">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-8 pt-20 pb-20 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/5 px-4 py-1.5 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-cyan-400" />
              <Eyebrow>About EntranceZone AI</Eyebrow>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Architect your future career
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400 text-glow">with AI + human mentors</span>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 mx-auto max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              EntranceZone AI combines Artificial Intelligence, Career Intelligence, data-driven insights and
              experienced human career mentors to help students make better decisions about careers, courses,
              colleges, entrance exams and future opportunities.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <button data-testid="about-hero-assessment-btn" onClick={startAssessment}
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-500 px-7 py-3.5 text-base font-bold text-[#041016] transition-transform hover:scale-105 glow-cyan">
                Start Your Career Assessment <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <button data-testid="about-hero-counselling-btn" onClick={exploreCounsellors}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-base font-bold text-slate-200 transition-all hover:border-cyan-400/50 hover:text-white">
                Explore Career Counselling
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Brand story */}
      <section className="mx-auto max-w-4xl px-4 sm:px-8 py-16">
        <Reveal><Eyebrow>Who we are</Eyebrow>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">An AI-powered career intelligence platform, launched by EntranceZone</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-slate-300 leading-relaxed">
            EntranceZone AI is built by a team with <span className="text-white font-semibold">15+ years of experience</span> in education,
            career guidance, admissions and student-focused digital platforms. We bring together AI Career Counselling,
            human career counsellors, AI Career Assessment, Career Intelligence, the Career Graph, college & course discovery,
            college comparison, college & rank predictors, entrance-exam guidance, scholarship & internship discovery,
            career planning and dedicated parent guidance — in one intelligent platform.
          </p>
          <div className="mt-8 rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-6 sm:p-8">
            <p className="text-sm text-slate-400">Our goal is to help every student move from</p>
            <p className="mt-2 font-display text-xl sm:text-2xl font-bold text-slate-300">“What should I do?”</p>
            <div className="my-3 flex items-center gap-2 text-cyan-400"><ArrowDown className="h-5 w-5" /></div>
            <p className="font-display text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
              “I understand my strengths, I know my options, and I have a roadmap.”
            </p>
          </div>
        </Reveal>
      </section>

      {/* Vision */}
      <section className="border-y border-white/8 bg-white/[0.015]">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 py-16">
          <Reveal>
            <div className="flex items-center gap-3"><Compass className="h-6 w-6 text-cyan-400" /><Eyebrow>Our vision</Eyebrow></div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">Make career decisions more intelligent, personalised and accessible</h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Career decisions should not be based only on marks, peer pressure, family expectations, popular careers,
              social trends or random internet searches. EntranceZone AI combines student-specific data, AI intelligence
              and human expertise to create a genuinely personalized approach to career planning.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Marks alone", "Peer pressure", "Family expectations", "Popular careers", "Social trends", "Random searches"].map((x) => (
                <span key={x} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-slate-400 line-through decoration-rose-400/60">{x}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Approach: AI + Human */}
      <section className="mx-auto max-w-6xl px-4 sm:px-8 py-16">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow>Our approach</Eyebrow>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">AI + Human Intelligence</h2>
          <p className="mt-4 text-slate-400">Two layers working together — the scale and speed of AI, guided by the judgment and empathy of experienced mentors.</p>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-cyan-400/20 bg-[#131C2E] p-7">
              <div className="flex items-center gap-3"><Cpu className="h-6 w-6 text-cyan-400" /><h3 className="font-display text-xl font-semibold">AI Intelligence</h3></div>
              <ul className="mt-5 space-y-2.5">
                {AI_POWERS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-slate-300"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" /> {p}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-emerald-400/20 bg-[#131C2E] p-7">
              <div className="flex items-center gap-3"><HeartHandshake className="h-6 w-6 text-emerald-400" /><h3 className="font-display text-xl font-semibold">Human Mentorship</h3></div>
              <ul className="mt-5 space-y-2.5">
                {HUMAN_POWERS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-slate-300"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" /> {p}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 15+ years */}
      <section className="border-y border-white/8">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-16 grid lg:grid-cols-12 gap-8 items-center">
          <Reveal className="lg:col-span-4 text-center lg:text-left">
            <p className="font-display text-6xl sm:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 to-indigo-400 text-glow">15+</p>
            <p className="mt-2 font-display text-xl font-bold">Years of experience in education &amp; career guidance</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p className="text-slate-300 leading-relaxed">
              EntranceZone AI is built on years of experience understanding students, parents, schools, colleges,
              entrance examinations, admissions, career decisions and education technology. That real-world experience
              is combined with modern AI to create a next-generation Career Intelligence Platform.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Students", "Parents", "Schools", "Colleges", "Entrance exams", "Admissions", "Career decisions", "EdTech"].map((x) => (
                <span key={x} className="rounded-full border border-cyan-400/15 bg-cyan-400/5 px-3 py-1 text-xs text-cyan-200">{x}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Meet our mentors */}
      <section className="border-b border-white/8">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-16">
          <Reveal className="max-w-2xl">
            <div className="flex items-center gap-2"><HeartHandshake className="h-6 w-6 text-cyan-400" /><Eyebrow>Meet our mentors</Eyebrow></div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">The people behind the platform</h2>
            <p className="mt-4 text-slate-400">A team of educators, admissions experts and subject mentors — combining decades of guidance experience with AI.</p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TEAM.map((m, i) => (
              <Reveal key={m.id || m.role} delay={i * 0.06}>
                <div data-testid="about-team-card" className="group h-full rounded-2xl border border-white/8 bg-[#131C2E] overflow-hidden transition-all hover:border-cyan-400/30 hover:-translate-y-1">
                  <div className="relative aspect-square overflow-hidden bg-[#0F1621]">
                    {m.img ? (
                      <img src={m.img} alt={`${m.name} — ${m.role}`} loading="lazy"
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div data-testid="about-team-placeholder" className="flex h-full w-full items-center justify-center"><Users className="h-16 w-16 text-slate-700" /></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#131C2E] via-transparent to-transparent" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-semibold text-white leading-snug min-h-[3.5rem] flex items-center">{m.name || "Mentor profile coming soon"}</h3>
                    <p className="text-sm text-cyan-300">{m.role}</p>
                    <p className="mt-2 text-xs text-slate-400 leading-relaxed">{m.cred || "Verified name, photo and credentials will be published here."}</p>
                    {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-cyan-400 hover:underline">LinkedIn →</a>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Career Graph */}
      <section className="mx-auto max-w-5xl px-4 sm:px-8 py-16">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2"><Network className="h-6 w-6 text-cyan-400" /><Eyebrow>Our technology</Eyebrow></div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">The Career Graph</h2>
          <p className="mt-4 text-slate-400">
            The Career Graph maps the relationships between every career decision — so the AI can generate deeply personalized recommendations.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-2 gap-y-3">
            {GRAPH.map((node, i) => (
              <div key={node} className="flex items-center gap-2">
                <span className={`rounded-full border px-4 py-2 text-sm font-medium ${i === 0 ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : i === GRAPH.length - 1 ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-200" : "border-white/12 bg-white/[0.03] text-slate-300"}`}>
                  {node}
                </span>
                {i < GRAPH.length - 1 && <ArrowRight className="h-4 w-4 text-slate-600" />}
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Feature cards */}
      <section className="border-y border-white/8 bg-white/[0.015]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-16">
          <Reveal className="max-w-2xl">
            <Eyebrow>What we help you do</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">One platform for the whole career journey</h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={(i % 3) * 0.06}>
                  <div data-testid="about-feature-card" className="h-full rounded-2xl border border-white/8 bg-[#131C2E] p-6 transition-all hover:border-cyan-400/30 hover:-translate-y-1">
                    <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border bg-gradient-to-br ${colorMap[f.color]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold">{f.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Students + Parents */}
      <section className="mx-auto max-w-6xl px-4 sm:px-8 py-16">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow>For everyone in the decision</Eyebrow>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">Built for students. Designed for parents.</h2>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/8 bg-[#131C2E] p-7">
              <div className="flex items-center gap-3"><GraduationCap className="h-6 w-6 text-cyan-400" /><h3 className="font-display text-xl font-semibold">Students need</h3></div>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Exploration", "Personalization", "Career discovery", "Mentorship", "Confidence"].map((x) => (
                  <span key={x} className="rounded-full bg-cyan-400/10 px-3 py-1.5 text-sm text-cyan-200">{x}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-white/8 bg-[#131C2E] p-7">
              <div className="flex items-center gap-3"><Users className="h-6 w-6 text-emerald-400" /><h3 className="font-display text-xl font-semibold">Parents need</h3></div>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Clarity", "Career options", "Education cost visibility", "College information", "Career roadmap", "Trusted guidance"].map((x) => (
                  <span key={x} className="rounded-full bg-emerald-400/10 px-3 py-1.5 text-sm text-emerald-200">{x}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}><p className="mt-6 text-center text-slate-400">EntranceZone AI brings both perspectives together.</p></Reveal>
      </section>

      {/* Philosophy */}
      <section className="border-y border-white/8 bg-white/[0.015]">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 py-16 text-center">
          <Reveal>
            <Lightbulb className="mx-auto h-8 w-8 text-amber-400" />
            <Eyebrow>Our philosophy</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl mx-auto">
              Don’t choose a career because everyone else is choosing it.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col items-center gap-2 text-slate-300">
              {["Who am I?", "What am I good at?", "What do I enjoy?", "What careers fit me?", "What should I study?", "Which colleges should I consider?", "What should I do next?"].map((q, i, arr) => (
                <div key={q} className="flex flex-col items-center">
                  <span className="rounded-xl border border-white/10 bg-[#131C2E] px-5 py-2.5 text-sm font-medium">{q}</span>
                  {i < arr.length - 1 && <ArrowDown className="my-1 h-4 w-4 text-cyan-500/70" />}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Trust & Transparency */}
      <section className="mx-auto max-w-4xl px-4 sm:px-8 py-14">
        <Reveal>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-7">
            <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-400" /><h3 className="font-display text-lg font-semibold">Trust &amp; transparency</h3></div>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              EntranceZone AI provides AI-powered guidance and educational insights to support career decision-making.
              AI recommendations and predictions are not guarantees of admission, employment, salary or career outcomes.
              Students should consider their individual circumstances and, where appropriate, consult qualified career
              professionals before making important decisions.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-10 sm:p-16 text-center">
          <div className="absolute inset-0 grid-texture opacity-30" />
          <div className="relative">
            <Target className="mx-auto h-12 w-12 text-cyan-400 mb-5" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl mx-auto">Your future is too important to guess.</h2>
            <p className="mt-4 text-slate-300 max-w-lg mx-auto">Architect your future career with AI + human mentors.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button data-testid="about-final-assessment-btn" onClick={startAssessment}
                className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-8 py-4 text-base font-bold text-[#041016] transition-transform hover:scale-105 glow-cyan">
                <Sparkles className="h-5 w-5" /> Start Free Career Assessment
              </button>
              <button data-testid="about-final-counsellors-btn" onClick={exploreCounsellors}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-bold text-slate-200 transition-all hover:border-cyan-400/50 hover:text-white">
                Explore Career Counsellors
              </button>
              <button data-testid="about-final-discover-btn" onClick={startAssessment}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-bold text-slate-200 transition-all hover:border-cyan-400/50 hover:text-white">
                Discover Your Career <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/8">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <Logo />
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
              <Link to="/about" className="hover:text-white transition-colors" data-testid="footer-about">About</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
              <Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </nav>
          </div>
          <p className="mt-8 text-center text-xs text-slate-500">
            © 2026 EntranceZone AI · Architecting Human Potential. Predictions are statistical estimates, not guarantees of admission or outcomes.
          </p>
        </div>
      </footer>
    </div>
  );
}
